<script lang="ts" setup>
import {ViewBlockInfo} from '~/api/service/block/list/index.service'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import LoadingTable from '~/components/loading/LoadingTable.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import {useTableColumn} from '~/composables/useTable'
import {blockListColumn} from '~/constants/blockListColumn.const'
import BcLink from '~/ui/Link/BcLink.vue'
import BcProgress from '~/ui/Progress/BcProgress.vue'
import BcTable from '~/ui/Table/BcTable.vue'
import {isEmpty} from '~/utils/judge'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

interface IProps {
  rows: ViewBlockInfo[]
  isLoading: boolean
  rowCnt: number
}

withDefaults(defineProps<IProps>(), {
  rows: () => []
})

const path = useRoute().fullPath.split('/').filter(elem => elem)
const localStorageKey = path.length > 1 ? `${path[0]}-${path[path.length - 1]}` : path[0]

const hideAge = ref()

if (process.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const exceptList = computed(() => {
  return hideAge.value ? ['AG'] : ['unixTimeStamp']
})

const toggle = () => {
  if (process.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}

const {column} = useTableColumn(blockListColumn, exceptList)
</script>

<template>
  <CustomSuspense
    :is-loading="isLoading"
    :is-empty="isEmpty(rows)"
  >
    <template #loading>
      <LoadingTable
        :row-cnt="rowCnt"
        :cols="column"
      />
    </template>
    <template #empty>
      <NoDataPreview/>
    </template>
    <template #default>
      <ClientOnly/>
      <BcTable
        :cols="column"
        :rows="rows"
        @change-table-data="toggle"
      >
        <template #BNO="{row}: {row: ViewBlockInfo}">
          <BcLink
            class="text-lg/[21px] font-bold"
            :content="row.BNO"
            :to="routes.getBlockDetailPageWithTab(row.BNO, blockDetailPageType.block)"
          >
            <template #prefix>
              <FontAwesomeIcon
                class="text-primary/40 group-hover:text-primary"
                icon="cube"
              />
            </template>
          </BcLink>
        </template>

        <template #TRXCNT="{row}: {row: ViewBlockInfo}">
          <BcLink
            :content="row.TRXCNT"
            :to="routes.getBlockDetailPageWithTab(row.BNO, blockDetailPageType.transactions)"
          />
        </template>

        <template #VD="{row}: {row: ViewBlockInfo}">
          <!--          <AddressCopyLink-->
          <!--            class="link"-->
          <!--            :address="row.VD"-->
          <!--            :tooltip="row.VD"-->
          <!--            hide-eoa-icon-->
          <!--          />-->
          <div
            class="link truncate"
            v-tooltip="row.VD"
          >
            {{ row.VD }}
          </div>
        </template>

        <template #gasUsed="{row}: {row: ViewBlockInfo}">
          <div class="flex flex-col items-center gap-2">
            <div class="flex gap-2">
              <span class="font-bold group-hover:text-primary">{{ row.gasUsed }}</span>
              <span v-tooltip="'Gas Used(%)'">({{ row.gasUsedPercent }}%)</span>
            </div>
            <BcProgress :value="row.gasUsedPercent"/>
          </div>
        </template>

        <template #burntFees="{row}: {row: ViewBlockInfo}">
          <div class="flex gap-2">
            <span class="group-hover:text-primary">{{ row.burntFees }}</span>
            <span v-tooltip="'Gas Fee(%)'">({{ row.burntFeesPercent ? `${row.burntFeesPercent}%` : '-' }})</span>
          </div>
        </template>
      </BcTable>
    </template>
  </CustomSuspense>
</template>

<style scoped lang="scss">
.link {
  max-width: 200px;
  @include over_lg {
    max-width: 240px;
  }
  @include over_2xl {
    max-width: 400px;
  }
}
</style>